import { useEffect } from "react";
import { useFieldArray, UseFormReturn } from "react-hook-form";
import { ProjectFormType } from "../../../../Common/hooks/useCreateProject";
import { Box } from "@mui/material";
import HeiUserTable from "../HeiUserTable/HeiUserTable";
import SearchAndAddUserSection from "../../../../Common/components/HeiUserSearch/SearchAndAddUserSection";
import { useSelector } from "react-redux";
import { selectHeiUser } from "../../../../Auth/store/accountSlice";
import { Roles } from "../../../../Common/models/roles";
import { ToolCapability, User } from "../../../../Common/models/FormModels";
import { EntityState } from "../../../../Common/api/models/EntityState";
import { useAtom } from "jotai";
import { selectedToolsAtom } from "../../../../Common/hooks/useToolsForSelect";
import { toolsDefaultRoles } from "../../../../Common/models/tools";

interface AddUsersStepProps {
  form: UseFormReturn<ProjectFormType>;
  useCustomRoleMapping?: boolean;
}
const userDefValues: User = {
  adPrinciple: "",
  email: "",
  name: "",
  roles: [],
  toolCaps: [],
  editStatus: EntityState.Add,
};

const formDefaultValues: ProjectFormType = {
  users: [userDefValues],
} as ProjectFormType;

export default function AddUserSection({ form, useCustomRoleMapping }: AddUsersStepProps) {
  const loggedUser = useSelector(selectHeiUser);
  const [selectedTools] = useAtom(selectedToolsAtom);
  const { control, getValues, setValue } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "users",
  });

  const getToolCapsForUser = (): ToolCapability[] => {
    return selectedTools.map((tool, index) => {
    
      const inviteMode = getValues(`tools.${index}.inviteMode`);
      const isAutomatic = inviteMode === "Automatically";
    
      const defaultRole = toolsDefaultRoles[tool.capabilityToolKey] ?? toolsDefaultRoles["DEFAULT"];
      
      return {
        toolDisplayName: tool.toolDisplayName,
        toolName: tool.toolName,
        capabilityId: tool.capabilityId,
        toolId: tool.toolId,
        toolProjectId: "",
        capabilityToolKey: tool.capabilityToolKey,
        hasAccess: isAutomatic, 
        availableRoles: tool.roles || [],
        toolCustomRole: defaultRole,
        toolCreationCompleted: true,
      };
    });
  };

  const formDefaultValuesWithTools: ProjectFormType = {
    users: [{
      ...userDefValues,
      toolCaps: getToolCapsForUser(),
    }],
  } as ProjectFormType;

  useEffect(() => {
    const users = getValues("users");
    const toolCaps = getToolCapsForUser();

    users.forEach((user, index) => {
      setValue(`users.${index}.toolCaps`, toolCaps);
    });
  }, [selectedTools, getValues, setValue]);

  useEffect(() => {
    if (getValues().users.length === 0) {

      const defaultRole: string = useCustomRoleMapping ? "Owner" : Roles["Product Owner"];
      
      const userToAdd: User = {
        name: loggedUser.name,
        adPrinciple: loggedUser.adPrinciple? loggedUser.adPrinciple : loggedUser.email,
        email: loggedUser.email,
        roles: [defaultRole] as Roles[], // Type assertion for role compatibility
        editStatus: EntityState.Add,
        toolCaps: getToolCapsForUser(),
      };
      append(userToAdd);
    }
  }, [useCustomRoleMapping]); // Add dependency to rerun when package type changes

  return (
    <Box>
      <SearchAndAddUserSection
        addUser={append}
        getUsers={getValues}
        formDefaultValues={formDefaultValuesWithTools}
        hideToolAccess
        useCustomRoleMapping={useCustomRoleMapping}
      />
      <HeiUserTable
        fields={fields}
        remove={remove}
        getValues={getValues}
        useCustomRoleMapping={useCustomRoleMapping}
        form={form}
      />
    </Box>
  );
}
